// Phase 3D.4-B - Pure, deterministic SIMULATED CAMPAIGN engine for the Savoie
// pilot. It transforms the CANONICAL output of
// buildGrowthRecommendation(opportunityResult) (see savoieRecommendation.js)
// into a single, strictly SIMULATED (fictional demo) campaign scenario.
//
// SCOPE / SAFETY CONTRACT:
//   * PURE, SYNCHRONOUS, DETERMINISTIC. No side effects.
//   * NO network / NO HTTP / NO DNS, NO DB, NO AI/provider, NO ad platform.
//   * NO Date.now(), NO randomness, NO environment read, NO storage.
//   * NO import with side effects (this module imports nothing).
//   * Does NOT render, does NOT create a page/route/component/backend.
//   * Does NOT create, publish or activate a real campaign.
//   * Does NOT predict, promise or guarantee any result (no ROI, no metric).
//   * Never mutates its input (recommendationResult / recommendationResult.meta).
//   * Fail-closed: any contract violation yields an explicit { status:"none" }.
//
// SEPARATION OF EVIDENCE vs ASSUMPTIONS:
//   * Evidence comes EXCLUSIVELY from the recommendation (periods + numbers).
//   * Campaign assumptions (period label, channels, budget, objective) come
//     EXCLUSIVELY from PILOT_SYNTHETIC_CAMPAIGN_SCENARIO below.
//   * Recommendation context fields are NEVER used to shape the campaign.

// --- Exact disclaimer (verbatim, required). -------------------------------
export const SIMULATED_CAMPAIGN_DISCLAIMER =
  "Campagne entièrement simulée à partir d'une recommandation déterministe et " +
  "de données synthétiques de démonstration. Le budget, les canaux, l'objectif " +
  "et la période sont des hypothèses fictives. Aucun résultat n'est prédit ou " +
  "garanti et aucune campagne réelle n'est créée, publiée ou activée.";

// --- Frozen synthetic demo scenario (a transparent, arbitrary hypothesis). -
// These values are NOT real, NOT analytical conclusions, NOT advice and are
// NOT derived from tourism_spend, from the recommendation or from any ad
// platform. They exist only to illustrate a fictional demo campaign.
export const PILOT_SYNTHETIC_CAMPAIGN_SCENARIO = Object.freeze({
  campaign_type: "market_awareness_simulation",
  channels: Object.freeze([
    Object.freeze({
      code: "paid_search_demo",
      label: "Recherche payante — hypothèse de démonstration",
      assumption: true,
      source: "synthetic_demo_scenario",
    }),
    Object.freeze({
      code: "paid_social_demo",
      label: "Réseaux sociaux payants — hypothèse de démonstration",
      assumption: true,
      source: "synthetic_demo_scenario",
    }),
  ]),
  budget: Object.freeze({
    amount: 12000,
    currency: "EUR",
    assumption: true,
    source: "synthetic_demo_scenario",
  }),
  objective: Object.freeze({
    code: "market_awareness",
    label:
      "Soutenir la notoriété de la destination auprès du marché source sélectionné.",
    assumption: true,
    source: "synthetic_demo_scenario",
  }),
  // No numeric forecast, no ROI, no expected metrics of any kind.
  expected_results: null,
});

// Deterministic reason tokens for the explicit "none" result.
export const CAMPAIGN_NONE_REASONS = Object.freeze({
  INVALID_INPUT: "invalid_input",
  NO_RECOMMENDATION: "no_recommendation",
  INVALID_STATUS: "invalid_status",
  MISSING_RECOMMENDATION_ID: "missing_recommendation_id",
  MISSING_OPPORTUNITY_ID: "missing_opportunity_id",
  INVALID_ACTION_TYPE: "invalid_action_type",
  INVALID_SIMULATION_FLAG: "invalid_simulation_flag",
  CAMPAIGN_NOT_NULL: "campaign_not_null",
  INVALID_META: "invalid_meta",
  INVALID_PROVENANCE: "invalid_provenance",
  INVALID_AUDIENCE: "invalid_audience",
  INVALID_PERIOD: "invalid_period",
  INVALID_EVIDENCE: "invalid_evidence",
  MISSING_DISCLAIMER: "missing_disclaimer",
});

// --- Pure validators ------------------------------------------------------
const _isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const _isNonEmptyStr = (v) => typeof v === "string" && v.length > 0;
const _isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

// "YYYY-MM" with month 01..12 -> true ; otherwise false.
function _isValidPeriod(str) {
  if (typeof str !== "string") return false;
  const m = /^(\d{4})-(\d{2})$/.exec(str);
  if (!m) return false;
  const month = Number(m[2]);
  return month >= 1 && month <= 12;
}

// Meta must mirror the frozen synthetic-demo contract (no transformation).
function _metaValid(meta) {
  if (!_isObj(meta)) return false;
  if (meta.is_demo !== true) return false;
  if (meta.provenance !== "synthetic_demo") return false;
  if (meta.provenance_label !== "DONNÉES DE DÉMONSTRATION") return false;
  if (meta.affiliation !== "none") return false;
  return true;
}

function _none(reason, meta) {
  return {
    status: "none",
    campaign: null,
    reason,
    meta: _isObj(meta) ? meta : null,
    simulation: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
  };
}

/**
 * Build at most ONE strictly SIMULATED campaign from the canonical output of
 * buildGrowthRecommendation(opportunityResult).
 * @param {object} recommendationResult - canonical recommendation result.
 * @returns simulated campaign object OR explicit { status:"none", ... }.
 */
export function buildSimulatedCampaign(recommendationResult) {
  const R = CAMPAIGN_NONE_REASONS;

  // 1. Basic shape.
  if (!_isObj(recommendationResult)) return _none(R.INVALID_INPUT, null);

  const rec = recommendationResult;
  const meta = rec.meta;

  // 2. Explicit "none" recommendation is propagated (no fabrication).
  if (rec.status === "none") return _none(R.NO_RECOMMENDATION, meta);

  // 3. Only a "recommended" recommendation can produce a simulated campaign.
  if (rec.status !== "recommended") return _none(R.INVALID_STATUS, meta);

  // 4. Recommendation identity must be usable.
  if (!_isNonEmptyStr(rec.id)) return _none(R.MISSING_RECOMMENDATION_ID, meta);
  if (!_isNonEmptyStr(rec.based_on_opportunity_id)) {
    return _none(R.MISSING_OPPORTUNITY_ID, meta);
  }

  // 5. Action-type consistency (closed enum for this phase).
  if (rec.action_type !== "audience_focus") {
    return _none(R.INVALID_ACTION_TYPE, meta);
  }

  // 6. The recommendation MUST still be non-simulated and non-activated.
  if (rec.simulation !== false) return _none(R.INVALID_SIMULATION_FLAG, meta);
  if (rec.campaign !== null) return _none(R.CAMPAIGN_NOT_NULL, meta);

  // 7. Demo meta / provenance contract (fail-closed, no transformation).
  if (!_metaValid(meta)) return _none(R.INVALID_META, meta);
  if (rec.provenance !== "synthetic_demo") return _none(R.INVALID_PROVENANCE, meta);

  // 8. Audience carried by the recommendation (never hardcoded here).
  const aud = rec.audience;
  if (
    !_isObj(aud) ||
    !_isNonEmptyStr(aud.source_market_code) ||
    !_isNonEmptyStr(aud.source_market_name)
  ) {
    return _none(R.INVALID_AUDIENCE, meta);
  }

  // 9. Simulation period is taken verbatim from the recommendation.
  if (!_isValidPeriod(rec.proposed_monitoring_period)) {
    return _none(R.INVALID_PERIOD, meta);
  }
  if (!_isValidPeriod(rec.evidence_period) || !_isValidPeriod(rec.comparison_period)) {
    return _none(R.INVALID_PERIOD, meta);
  }

  // 10. Evidence structure + mandatory finite numeric fields (from the reco).
  const ev = rec.evidence_used;
  if (!_isObj(ev)) return _none(R.INVALID_EVIDENCE, meta);
  const {
    visitor_arrivals,
    growth_percent,
    market_share_percent,
  } = ev;
  if (
    !_isFiniteNum(visitor_arrivals) ||
    !_isFiniteNum(growth_percent) ||
    !_isFiniteNum(market_share_percent)
  ) {
    return _none(R.INVALID_EVIDENCE, meta);
  }

  // 11. Recommendation disclaimer must be present (contract coherence).
  if (!_isNonEmptyStr(rec.disclaimer)) return _none(R.MISSING_DISCLAIMER, meta);

  // --- Build the single simulated campaign ------------------------------
  const S = PILOT_SYNTHETIC_CAMPAIGN_SCENARIO;
  const period = rec.proposed_monitoring_period;
  const marketName = aud.source_market_name;
  const marketCode = aud.source_market_code;

  // Fresh (non-frozen) copies so the returned object owns its own data and the
  // frozen scenario is never leaked by reference.
  const channels = S.channels.map((c) => ({
    code: c.code,
    label: c.label,
    assumption: c.assumption,
    source: c.source,
  }));
  const budget = {
    amount: S.budget.amount,
    currency: S.budget.currency,
    assumption: S.budget.assumption,
    source: S.budget.source,
  };
  const objectives = [
    {
      code: S.objective.code,
      label: S.objective.label,
      assumption: S.objective.assumption,
      source: S.objective.source,
    },
  ];

  const name = `Simulation — Focus ${marketName} — ${period}`;

  const userFacingText =
    `Simulation de campagne pour le marché ${marketName} (${marketCode}) ` +
    `sur la période ${period}. Scénario fictif de démonstration : le budget, ` +
    `les canaux, l'objectif et la période sont des hypothèses synthétiques. ` +
    `Aucune campagne réelle n'est créée, publiée ou activée.`;

  // Campaign meta: a NON-MUTATING copy of the recommendation meta with the
  // simulation flags set. Preserves is_demo / provenance / label / affiliation.
  const campaignMeta = {
    ...meta,
    simulation: true,
    simulation_label: "SIMULATION",
  };

  // Structured assumptions (NOT evidence). Every entry is explicitly fictional.
  const assumptions = [
    {
      kind: "budget",
      detail: "Budget de campagne fixé arbitrairement pour la démonstration.",
      assumption: true,
      source: "synthetic_demo_scenario",
      real_data: false,
      predictive: false,
    },
    {
      kind: "channels",
      detail: "Canaux publicitaires fictifs, sans lien avec une plateforme réelle.",
      assumption: true,
      source: "synthetic_demo_scenario",
      real_data: false,
      predictive: false,
    },
    {
      kind: "objective",
      detail: "Objectif de notoriété illustratif, non contractuel.",
      assumption: true,
      source: "synthetic_demo_scenario",
      real_data: false,
      predictive: false,
    },
    {
      kind: "period",
      detail:
        "Période de simulation reprise de la recommandation ; ce n'est ni une " +
        "date de lancement réelle ni une projection.",
      assumption: true,
      source: "synthetic_demo_scenario",
      real_data: false,
      predictive: false,
    },
  ];

  return {
    status: "simulated",
    id: `campaign-sim:${rec.id}`,
    based_on_recommendation_id: rec.id,
    based_on_opportunity_id: rec.based_on_opportunity_id,
    campaign_type: S.campaign_type,
    name,
    audience: {
      source_market_code: marketCode,
      source_market_name: marketName,
    },
    period: {
      month: period,
      type: "synthetic_demo_period",
    },
    channels,
    budget,
    objectives,
    // No forecast whatsoever: no ROI, no impressions/clicks/visitors/conversions.
    expected_results: null,
    // Evidence ONLY (strictly from the recommendation, never assumptions).
    evidence_reference: {
      evidence_period: rec.evidence_period,
      comparison_period: rec.comparison_period,
      growth_percent: ev.growth_percent,
      visitor_arrivals: ev.visitor_arrivals,
      market_share_percent: ev.market_share_percent,
    },
    assumptions,
    provenance: "synthetic_demo",
    source_meta: meta,
    meta: campaignMeta,
    simulation: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
    disclaimer: SIMULATED_CAMPAIGN_DISCLAIMER,
    user_facing_text: userFacingText,
  };
}

export const _campaignInternals = {
  _isValidPeriod,
  _metaValid,
};
